import type { FileFormatScope } from '@steerfi/shared';
import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type FileFormatApiRow,
  type FileFormatListQuery,
  type FileFormatListSortBy,
  getFileFormat,
  listFileFormats,
} from '@/api/file-formats';
import type { DataTableResponse } from '@/components/data-table';

// Mirrors the backend DTO defaults in
// services/api-server/src/dtos/file-format.dto.ts (queryFileFormatSchema).
// Keep both ends in sync until the constants live in @steerfi/shared.
const DEFAULTS = {
  PAGE: 1,
  PAGE_SIZE: 25,
  SORT_BY: 'format_name' as const,
  ORDER: 'asc' as const,
  STATUS: 'active' as const,
  SEARCH_DEBOUNCE_MS: 300,
} as const;

const FILE_FORMATS_RESULT_CACHE_MAX = 20;

export type FileFormatScopeFilter = 'all' | 'system' | 'provider' | 'client';
export type FileFormatStatusFilterUi = 'active' | 'deleted';

// Mapping is data, not control flow: extending this table with a new UI
// filter is the only edit needed. The current vocabulary is a strict
// toggle (Active | Deleted) -- there is no UI affordance for "show
// both", so the wire form is always a single string. If we ever
// reintroduce a synthetic union state, this table is where it lives:
// e.g. `all: ['active', 'deleted']` would expand into the array form
// the backend already supports (see applyStatusFilter in
// services/api-server/src/repositories/file-format.repository.ts).
const STATUS_UI_TO_API: Record<
  FileFormatStatusFilterUi,
  NonNullable<FileFormatListQuery['status']>
> = {
  active: 'active',
  deleted: 'deleted',
};

// Strip undefined / empty-string entries so the resulting object
// satisfies `exactOptionalPropertyTypes` and avoids serializing empty params.
// Input is permissive (any field may be undefined) and the output narrows
// to the strict shape that the API client expects. The empty-string guard
// stays as URL hygiene against future direct setFilters({ search: '' })
// writes; the input type intentionally excludes null since FileFormatListQuery
// has no nullable fields and the runtime never produced one.
const compactQuery = <T extends object>(input: { [K in keyof T]: T[K] | undefined }): T => {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value === undefined || value === '') continue;
    out[key] = value;
  }
  return out as T;
};

// Encapsulates the two race concerns so the call site can ask a single
// question -- "is this result still wanted?" -- via guard.isCurrent().
//
// `signal`  : cancels live fetches we can still abort (fast-typing search).
// `epoch`   : invalidates microtasks already past the network boundary
//             (e.g. a fetch that resolves AFTER refetch() bumped the epoch
//             on Add/Edit/Delete success).
//
// Both checks live behind one predicate so fetchData reads as intent,
// not as a tangle of state-mutation guards.
const beginFetch = (
  inFlightRef: MutableRefObject<AbortController | null>,
  epochRef: MutableRefObject<number>
) => {
  inFlightRef.current?.abort();
  const controller = new AbortController();
  inFlightRef.current = controller;
  const startedAt = epochRef.current;
  return {
    signal: controller.signal,
    isCurrent: () => !controller.signal.aborted && startedAt === epochRef.current,
    isAbort: (err: unknown) => err instanceof DOMException && err.name === 'AbortError',
  };
};

// FIFO eviction when the cache is at cap. Map iteration order is
// insertion order, so keys().next() yields the oldest entry.
// Pulled out of fetchData so the success path stays declarative.
const writeToCache = <K, V>(cache: Map<K, V>, key: K, value: V, max: number): void => {
  if (cache.size >= max) {
    const firstKey = cache.keys().next().value;
    if (firstKey !== undefined) cache.delete(firstKey);
  }
  cache.set(key, value);
};

export interface UseFileFormatsOptions {
  partyId?: string;
  scope?: FileFormatScope;
}

interface UseFileFormatsReturn {
  tableData: DataTableResponse<FileFormatApiRow> | undefined;
  isLoading: boolean;
  error: string | null;
  filters: FileFormatListQuery;
  scopeUi: FileFormatScopeFilter;
  statusUi: FileFormatStatusFilterUi;
  setSearch: (v: string) => void;
  setScope: (v: FileFormatScopeFilter) => void;
  setStatus: (v: FileFormatStatusFilterUi) => void;
  setPage: (v: number) => void;
  setSort: (sortBy: string, order: 'asc' | 'desc') => void;
  refetch: () => void;
}

export const useFileFormats = (options?: UseFileFormatsOptions): UseFileFormatsReturn => {
  const [tableData, setTableData] = useState<DataTableResponse<FileFormatApiRow> | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // `filters` deliberately omits `status`: the UI variant is canonical
  // (see STATUS_UI_TO_API comment above) and tracked separately in
  // `statusUi` below. The query value is derived inside fetchData.
  const [filters, setFilters] = useState<FileFormatListQuery>({
    page: DEFAULTS.PAGE,
    pageSize: DEFAULTS.PAGE_SIZE,
    sortBy: DEFAULTS.SORT_BY,
    order: DEFAULTS.ORDER,
  });
  const [statusUi, setStatusUiState] = useState<FileFormatStatusFilterUi>(DEFAULTS.STATUS);
  const [debouncedSearch, setDebouncedSearch] = useState<string | undefined>(undefined);

  // Refs survive renders without triggering one. `hasLoadedRef` gates the
  // loading spinner so it only renders for the very first fetch -- every
  // subsequent refetch (search debounce, filter change, sort, paginate)
  // keeps the previous rows on screen, letting DataTable's content
  // transition swap content smoothly instead of collapsing the table to
  // a single "Loading..." row. `resultsCacheRef` shortcuts the network for
  // query combos we've already seen, capped at FILE_FORMATS_RESULT_CACHE_MAX
  // entries with simple FIFO eviction (matches payments.tsx).
  // `inFlightRef` + `epochRef` power the race guard (see beginFetch above).
  const hasLoadedRef = useRef(false);
  const tableDataRef = useRef(tableData);
  tableDataRef.current = tableData;
  const resultsCacheRef = useRef(new Map<string, DataTableResponse<FileFormatApiRow>>());
  const inFlightRef = useRef<AbortController | null>(null);
  const epochRef = useRef(0);

  // Debounce only the search field. Other filter changes (page, sort, scope, status)
  // remain instant since they are discrete user actions.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(filters.search), DEFAULTS.SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [filters.search]);

  const { page, pageSize, sortBy, order, scope } = filters;

  const pinnedPartyId = options?.partyId;
  const pinnedScope = options?.scope;

  const fetchData = useCallback(async () => {
    const query = compactQuery<FileFormatListQuery>({
      page,
      pageSize,
      sortBy,
      order,
      // Forward-only mapping: statusUi is the user's canonical selection,
      // collapsed into the wire shape here. We never round-trip the
      // backend value back into a UI variant (would lose the 'all' vs
      // 'include_deleted' distinction).
      status: STATUS_UI_TO_API[statusUi],
      scope: pinnedScope ?? scope,
      search: debouncedSearch,
      party_id: pinnedPartyId,
    });
    const cacheKey = JSON.stringify(query);
    const cached = resultsCacheRef.current.get(cacheKey);
    if (cached !== undefined) {
      setTableData(cached);
      setError(null);
      hasLoadedRef.current = true;
      return;
    }

    const guard = beginFetch(inFlightRef, epochRef);
    const isInitialLoad = !hasLoadedRef.current;
    const isEmptyTransition =
      hasLoadedRef.current && (tableDataRef.current?.data.length ?? 0) === 0;
    if (isInitialLoad || isEmptyTransition) setIsLoading(true);
    setError(null);

    try {
      const result = await listFileFormats(query, { signal: guard.signal });
      if (!guard.isCurrent()) return;
      const next: DataTableResponse<FileFormatApiRow> = {
        data: result.data,
        pagination: {
          page: result.pagination.page,
          pageSize: result.pagination.pageSize,
          total: result.pagination.total,
          totalPages: result.pagination.totalPages,
        },
      };
      setTableData(next);
      hasLoadedRef.current = true;
      writeToCache(resultsCacheRef.current, cacheKey, next, FILE_FORMATS_RESULT_CACHE_MAX);
    } catch (e) {
      if (guard.isAbort(e)) return;
      setError(e instanceof Error ? e.message : 'Failed to load file formats');
      setTableData(undefined);
    } finally {
      if ((isInitialLoad || isEmptyTransition) && guard.isCurrent()) setIsLoading(false);
    }
  }, [page, pageSize, sortBy, order, statusUi, scope, debouncedSearch, pinnedPartyId, pinnedScope]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Public refetch -- used by Add/Edit/Delete onSuccess handlers. Clears
  // the cache so the next fetch pulls fresh server state; we keep
  // `hasLoadedRef = true` on purpose so the post-write refresh swaps in
  // place (no spinner row) just like normal filter changes. Bumping the
  // epoch first ensures any fetch that started before this refetch will
  // observe `guard.isCurrent() === false` and discard its result, so a
  // stale-as-of-the-write response can't sneak into the freshly-cleared
  // cache.
  const refetch = useCallback(() => {
    epochRef.current++;
    resultsCacheRef.current.clear();
    fetchData();
  }, [fetchData]);

  const setSearch = useCallback((search: string) => {
    setFilters((prev) => {
      const trimmed = search.trim();
      const next: FileFormatListQuery = { ...prev, page: DEFAULTS.PAGE };
      if (trimmed) {
        next.search = trimmed;
      } else {
        delete next.search;
      }
      return next;
    });
  }, []);

  const setScope = useCallback((scope: FileFormatScopeFilter) => {
    setFilters((prev) => {
      const next: FileFormatListQuery = { ...prev, page: DEFAULTS.PAGE };
      if (scope === 'all') {
        delete next.scope;
      } else {
        next.scope = scope;
      }
      return next;
    });
  }, []);

  const setStatus = useCallback((status: FileFormatStatusFilterUi) => {
    setStatusUiState(status);
    setFilters((prev) => ({ ...prev, page: DEFAULTS.PAGE }));
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const setSort = useCallback((sortBy: string, order: 'asc' | 'desc') => {
    setFilters((prev) => ({
      ...prev,
      sortBy: sortBy as FileFormatListSortBy,
      order,
      page: DEFAULTS.PAGE,
    }));
  }, []);

  const scopeUi: FileFormatScopeFilter = useMemo(
    () => (filters.scope ?? 'all') as FileFormatScopeFilter,
    [filters.scope]
  );

  return {
    tableData,
    isLoading,
    error,
    filters,
    scopeUi,
    statusUi,
    setSearch,
    setScope,
    setStatus,
    setPage,
    setSort,
    refetch,
  };
};

interface UseFileFormatReturn {
  row: FileFormatApiRow | undefined;
  isLoading: boolean;
  error: string | null;
  notFound: boolean;
  refetch: () => void;
}

// Mirrors `useProvider`: fetches a single record by id, exposing the same
// (loading / error / notFound) tri-state so the detail page renders one of
// three views without ambiguity. We don't have a typed API error class
// here (the file-formats client throws generic Error from the shared
// keycloak fetch), so 404 is detected via the backend's "not found"
// message -- service layer always uses NotFoundError('File format not found'),
// which surfaces verbatim in `Error.message`.
// Single source of truth for "will this id trigger a fetch?". Used both
// to seed initial state (so the early-return path doesn't need to flip
// isLoading from `true` -> `false`) and to gate the fetch itself.
const isFetchableId = (id: number | undefined): id is number => id != null && !Number.isNaN(id);

export const useFileFormat = (id: number | undefined): UseFileFormatReturn => {
  const [row, setRow] = useState<FileFormatApiRow | undefined>();
  const [isLoading, setIsLoading] = useState(() => isFetchableId(id));
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(() => !isFetchableId(id));

  const inFlightRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    if (!isFetchableId(id)) {
      setRow(undefined);
      setNotFound(true);
      return;
    }

    inFlightRef.current?.abort();
    const controller = new AbortController();
    inFlightRef.current = controller;

    try {
      setIsLoading(true);
      setError(null);
      setNotFound(false);
      setRow(undefined);
      const result = await getFileFormat(id, { signal: controller.signal });
      if (controller.signal.aborted) return;
      setRow(result);
    } catch (e: unknown) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      const message = e instanceof Error ? e.message : 'Failed to load file format';
      if (/not\s*found|HTTP\s*404/i.test(message)) {
        setNotFound(true);
      } else {
        setError(message);
      }
    } finally {
      if (!controller.signal.aborted) setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
    return () => inFlightRef.current?.abort();
  }, [fetchData]);

  return { row, isLoading, error, notFound, refetch: fetchData };
};
